// The **CustomFitting** component is a React-based UI module designed to showcase a personalized fitting experience.
// It features a **hero section** with a video banner and interactive buttons, dynamically rendered from **customFitData**.
// The **Why Custom Fit** section highlights the benefits of custom fitting with a title, description, and a separator.
//  The **How Custom Fit Works** section includes a slider with images and descriptions, navigable using previous
//  and next buttons. The **Custom Fitting Technology** section presents advanced fitting techniques. Users
//  can book appointments via the **Book Now** section with customizable button styles. A **FAQs section**
//  with an accordion-style layout provides additional information. Lastly, the **Shop by Category** section
//  links users to relevant product categories. The entire component is styled using Tailwind CSS and integrates
//   Swiper for interactive elements.

import Link from 'components/commercetools-ui/atoms/link';
import React, { useEffect, useRef, useState } from 'react';
import { CustomFittingDataSource, CustomFittingFAQS, HeroButton, ShopByCategoryContent } from 'types/next';
import SwiperType, { Navigation, Pagination, Thumbs } from 'swiper';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import CustomFittingTechnology from './custom-fit-technology';
import TrustBox from '../../trustpilot';

interface CustomFitProps {
  customFitData: CustomFittingDataSource;
}

const CustomFitting = ({ customFitData }: CustomFitProps) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const herovideo = customFitData?.heroBannerSlot?.heroBanner?.heroVideo;
  const heroMobilevideo = customFitData?.heroBannerSlot?.heroBanner?.heroMobileVideo;
  const heroBanner = `https://${herovideo?.defaultHost}/v/${herovideo?.endpoint}/${herovideo?.name}/mp4_720p`;
  const heroMobileBanner = `https://${heroMobilevideo?.defaultHost}/v/${heroMobilevideo?.endpoint}/${heroMobilevideo?.name}/mp4_720p`;
  const [openIndex, setOpenIndex] = useState(null);

  const contentItems = customFitData?.customFitWorksSlot?.customFitWorks?.contents || [];
  const totalItems = contentItems.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const customBookingLink = process.env.NEXT_PUBLIC_CUSTOM_BOOKING_URL;
  // Move to previous slide (loop to last item if at the start)
  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? totalItems - 1 : prevIndex - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex === totalItems - 1 ? 0 : prevIndex + 1));
  };

  const toggleAccordion = (index: any) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const customFitWorks = customFitData?.customFitWorksSlot?.customFitWorks;

  const bg = `https://${customFitWorks?.backgroundImage?.defaultHost}/i/${customFitWorks?.backgroundImage?.endpoint}/${customFitWorks?.backgroundImage?.name}`;
  const bgClub = `https://${customFitWorks?.backgroundClubImage?.defaultHost}/i/${customFitWorks?.backgroundClubImage?.endpoint}/${customFitWorks?.backgroundClubImage?.name}?qlt=100`;
  const bgColor = customFitWorks?.backgroundColor;

  return (
    <>
      <style>
        {`
          .howcustomfitworks-section{ 
          background-image: url(${bg});
          position: relative;
          background-color: ${bgColor};
          background-size: 40%;
          }

         .howcustomfitworks-section:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: url(${bgClub});
          background-position: -20% center;
          background-size: 50%;
          animation: grow 20s infinite ease;
        }

        .howcustomfitworks-section:after {
          content: '';
          // display: block;
          position: absolute;
          width: 100%;
          height: 50px;
          background: #1a4075;
          top: -10px;
          z-index: -20;
        }

        .slide-content h2{
          transform: all 0.3s ease-in;
          -webkit-transition: all 0.3s ease-in;
          -moz-transition: all 0.3s ease-in;
          -ms-transition: all 0.3s ease-in;
          -o-transition: all 0.3s ease-in;
        }

        .slide-content:hover h2{
            letter-spacing: 5px;
            cursor: pointer
        }

        @keyframes grow {
  0% {
    background-size: 55%;
}
25% {
    background-size: 60%;
}
50% {
    background-size: 65%;
}
75% {
    background-size: 60%;
}
100% {
    background-size: 55%;
}
}
        
      `}
      </style>

      <div className="herobanner-section relative w-full max-w-full">
        {herovideo?.defaultHost && herovideo?.endpoint && herovideo?.name && (
          <video
            src={heroBanner}
            loop
            autoPlay
            muted
            playsInline
            className="hidden h-auto w-full object-contain sm:h-[400px] sm:object-cover md:block"
            controls
          />
        )}
        {heroMobilevideo?.defaultHost && heroMobilevideo?.endpoint && heroMobilevideo?.name && (
          <video
            src={heroMobileBanner}
            loop
            autoPlay
            muted
            playsInline
            className="h-auto w-screen object-contain sm:h-[400px] sm:object-cover md:hidden"
            controls
          />
        )}
        <div className="hidden sm:block">
          {customFitData?.heroBannerSlot?.heroBanner?.heroButton?.map(
            (button: HeroButton, index: number) =>
              button.isEnabled && (
                <Link link={customBookingLink} key={index}>
                  <button
                    className=" absolute bottom-[50px] left-[50%] z-10 my-auto hidden max-h-[55px] -translate-x-1/2 cursor-pointer rounded-md px-12 py-3 text-center font-body text-[1.3rem] font-semibold uppercase leading-[2.4rem] tracking-[7px] transition-all sm:block"
                    style={{
                      backgroundColor: button.bgColor,
                      color: button.textColor,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = button.hoverBgColor;
                      e.currentTarget.style.color = button.hoverTextColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = button.bgColor;
                      e.currentTarget.style.color = button.textColor;
                    }}
                  >
                    {button.text}
                  </button>
                </Link>
              ),
          )}
        </div>
      </div>

      {/* Button for Mobile (Below Video) */}
      <div className="book-now-section mb-15 mt-26 flex w-full flex-col items-center justify-center gap-y-10 px-4 sm:hidden">
        {customFitData?.heroBannerSlot?.heroBanner?.heroButton?.map(
          (button: HeroButton, index: number) =>
            button.isEnabled && (
              <Link link={customBookingLink} key={index} className="flex w-full justify-center">
                <button
                  className="mb-10 w-full max-w-[90%] cursor-pointer rounded-md px-12 py-3 text-center font-body text-[1.3rem] font-semibold uppercase leading-[2.4rem] tracking-[7px] md:max-w-[300px] lg:max-w-[350px] xl:max-w-[400px]"
                  style={{
                    backgroundColor: button.bgColor,
                    color: button.textColor,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = button.hoverBgColor;
                    e.currentTarget.style.color = button.hoverTextColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = button.bgColor;
                    e.currentTarget.style.color = button.textColor;
                  }}
                >
                  {button.text}
                </button>
              </Link>
            ),
        )}
      </div>
      <div className="whyCustomFit-section flex w-full flex-col items-center gap-y-10 px-4 pb-20 pt-15  lg:mx-auto">
        <div className="m-0 px-[7%] text-center xl:mx-63 2xl:mx-150">
          <h3 className=" mt-20 block px-20  py-2 text-center font-body text-25 font-normal uppercase leading-[50px] tracking-wide text-[#1A1871] sm:inline sm:px-6 sm:py-3 md:mx-75  lg:text-38 ">
            {customFitData?.whyCustomFit?.title}
          </h3>
          <hr className="mx-auto my-[28px] h-[1px] max-w-[960px] bg-[#cfc9c4]" />
          <p
            className="mt-24 text-center font-body text-16 font-normal leading-[19.2px] sm:px-10 sm:text-justify md:px-20 lg:px-50 xl:px-90 2xl:px-80"
            style={{ fontWeight: '400px' }}
          >
            {customFitData?.whyCustomFit?.description}
          </p>
        </div>
      </div>

      <div className="howcustomfitworks-section mb-30  flex w-full flex-col items-center  justify-center py-15">
        <div className=" my-20 flex w-[70%] max-w-[800px] justify-center bg-white text-24 font-normal uppercase leading-[50px] text-[#1A1871] md:h-[50px] lg:text-38">
          <h3 className="text-center">{customFitData?.customFitWorksSlot?.customFitWorks?.heading}</h3>
        </div>

        <div className="flex w-full items-center  justify-center">
          <div className="relative mt-20 w-full max-w-[1400px] overflow-hidden ">
            {/* Navigation Buttons */}
            <div className="absolute left-0  right-0 top-1/2 flex w-full -translate-y-1/2 justify-between px-4">
              <button
                className="flex h-48 w-50  items-center justify-center  rounded-md bg-white p-4 shadow-md transition-all duration-300 hover:bg-[#151461] md:h-50 md:w-50"
                onClick={handlePrev}
              >
                <ChevronLeftIcon className="h-40 w-45 text-[#1A1871] hover:text-black md:h-40 md:w-40" />
              </button>
              <button
                className="flex h-48 w-50  items-center justify-center  rounded-md bg-white shadow-md transition-all duration-300 hover:bg-[#151461] md:h-50 md:w-50"
                onClick={handleNext}
              >
                <ChevronRightIcon className="h-40 w-45 text-[#1A1871] hover:text-black  md:h-40 md:w-40" />
              </button>
            </div>

            {/* Slide Content */}
            <div className="slide-content mx-auto flex w-[90%] flex-col items-center justify-between rounded-lg p-6 text-center shadow-md md:flex-row">
              <div className="mx-auto w-full p-16 md:float-left md:mx-0 md:w-1/2">
                <img
                  src={`https://${contentItems[currentIndex].image.defaultHost}/i/${contentItems[currentIndex].image.endpoint}/${contentItems[currentIndex].image.name}`}
                  alt={contentItems[currentIndex].title}
                  className="h-full w-full rounded-md object-cover  md:p-0"
                />
              </div>

              <div className="w-full items-center bg-[#1A4075] px-20 py-15 tracking-normal md:float-right md:mt-0 md:h-1/2 md:w-1/2 md:max-w-none">
                <h2 className="py-10 text-center text-[1.4rem] font-bold uppercase leading-5 tracking-widest text-white md:text-left">
                  {contentItems[currentIndex].title}
                </h2>
                <p className="text-md mt-2 text-center font-normal text-white md:text-left">
                  {contentItems[currentIndex].description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomFittingTechnology customFitTechnology={customFitData?.customFitTechnology} />

      <div className="book-now-section mb-15 flex w-full flex-col items-center justify-center gap-y-10 px-4">
        <Link link={customBookingLink} className="flex w-full justify-center">
          <button
            className="relative z-10 mx-auto mb-2 mt-12 block max-h-[55px] w-[90%] rounded-md bg-[#1a4075] p-3 text-center font-body text-lg font-semibold uppercase leading-[2.4rem] tracking-[7px] text-white sm:w-[220px] sm:text-[1.3rem]"
            style={{
              backgroundColor: customFitData?.bookNow?.bgColor,
              color: customFitData?.bookNow?.textColor,
              border: '1px dashed #1a4075',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = customFitData?.bookNow?.hoverBgColor;
              e.currentTarget.style.color = customFitData?.bookNow?.hoverTextColor;
              e.currentTarget.style.border = '1px dashed #1a4075'; // Ensure border is kept on hover
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = customFitData?.bookNow?.bgColor;
              e.currentTarget.style.color = customFitData?.bookNow?.textColor;
            }}
          >
            {customFitData?.bookNow?.buttonName}
          </button>
        </Link>
        <p className="font-14 font-body font-normal leading-5">
          <em>{customFitData?.bookNow?.freeInstore}</em>
        </p>
      </div>

      <div className="faqs-section mx-auto mb-20 w-full max-w-[1140px] px-4 pb-20 pt-15">
        <div className="mb-10 flex justify-center">
          <h3 className="font-body text-24 font-normal leading-[50px] text-[#1A1871] lg:text-38">FAQS</h3>
        </div>
        <div className="w-full">
          {customFitData?.faqs &&
            customFitData?.faqs?.length > 0 &&
            customFitData?.faqs?.map((faq, index) => {
              const isOpen = openIndex === index;

              return (
                <div
                  key={index}
                  className={`my-5 flex flex-col items-center justify-center rounded-md border border-dashed border-gray-300 pl-20 pr-10 md:pl-40 md:pr-40 ${
                    isOpen ? 'bg-[#1A4075] pt-5 text-white' : 'bg-white text-black'
                  }`}
                >
                  <button
                    className="group relative flex h-60 w-full items-center justify-center p-20 py-20 text-16 font-semibold transition-colors duration-300 ease-out md:p-5"
                    onClick={() => toggleAccordion(index)}
                  >
                    <span className="relative flex items-center justify-center font-body text-18 font-medium leading-5 transition-all duration-300">
                      <span
                        className={`absolute left-[-30px] top-1/2 hidden -translate-y-1/2 transform text-18 font-medium leading-5  text-[#1a4075] transition-all duration-200 ease-in-out md:block ${
                          isOpen ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
                        }`}
                      >
                        [
                      </span>
                      {faq?.question?.toLocaleUpperCase()}
                      <span
                        className={`absolute right-[-30px] top-1/2 hidden -translate-y-1/2 transform text-18 font-medium leading-5 transition-all duration-200 ease-in-out md:block ${
                          isOpen ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
                        }`}
                      >
                        ]
                      </span>
                    </span>
                    <span
                      className={`absolute ${
                        isOpen ? 'text-white' : 'text-[#1a4075]'
                      }  right-0 top-1/2 -translate-y-1/2 transform text-30 font-medium leading-5  transition-colors duration-300`}
                    >
                      {isOpen ? '-' : '+'}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="flex items-center justify-center px-5  pb-4 text-center font-body text-16 font-normal leading-[19.2px] transition-all duration-300 ease-in-out">
                      {faq?.answer}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      <div className="my-20 w-full bg-[#f9f9f9] p-10 py-20">
        <TrustBox showVertical={false} />
      </div>

      <div className="shopByCategory-section  w-full px-2 pb-20 pt-15 md:mx-4">
        <div className="mb-10 flex justify-center xl:mb-30">
          <h3 className="text-center font-body text-24 font-normal uppercase leading-6 text-[#1A1871] lg:text-38">
            {customFitData?.shopByCategory?.overallTitle}
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {customFitData?.shopByCategory?.content &&
            customFitData.shopByCategory.content.map((item: ShopByCategoryContent, index: number) => (
              <Link key={index} link={item?.link} className="flex w-full flex-col items-center">
                {/* Image : Need to work on it */}
                {
                  <img
                    src={`https://${item.image.defaultHost}/i/${item.image.endpoint}/${item.image.name}`}
                    alt={item?.name}
                    className="max-h-[160px] w-auto object-contain md:max-h-[200px] lg:max-h-[150px]"
                  />
                }
                <div className="border-t py-2 text-center font-body text-16 font-semibold hover:text-[#1A1871]">
                  {item?.name}
                </div>
              </Link>
            ))}
        </div>
      </div>
    </>
  );
};

export default CustomFitting;
